import { z } from "zod";

export const TYPES_PROJET = [
  "renovation_complete",
  "cuisine",
  "salle_de_bain",
  "metallerie",
  "autre",
] as const;

export const DELAIS = [
  "immediat",
  "moins_3_mois",
  "moins_6_mois",
  "plus_6_mois",
  "renseignement",
] as const;

export const BUDGETS = [
  "moins_10k",
  "10_25k",
  "25_50k",
  "50_100k",
  "plus_100k",
  "ne_sait_pas",
] as const;

/** Étapes du suivi d'une demande, dans l'ordre où elles se succèdent. */
export const STATUTS_LEAD = ["nouveau", "contacte", "visite", "gagne", "perdu"] as const;

export type TypeProjet = (typeof TYPES_PROJET)[number];
export type Delai = (typeof DELAIS)[number];
export type Budget = (typeof BUDGETS)[number];
export type StatutLead = (typeof STATUTS_LEAD)[number];

// ---------------------------------------------------------------------------
// Libellés
//
// Ces tables vivent ici et non dans l'API parce qu'elles ont deux lecteurs :
// l'email de notification (`api/src/services/notification-lead.ts`) et les
// écrans de suivi des demandes. Une seconde copie divergerait au premier
// budget ajouté, et l'email annoncerait alors autre chose que l'écran.
//
// Le typage en `Record<Union, string>` n'est pas décoratif : ajouter une
// valeur à `BUDGETS` sans lui donner de libellé casse la compilation, ce qui
// est exactement le moment où l'on veut l'apprendre.
// ---------------------------------------------------------------------------

export const LIBELLES_PROJET: Record<TypeProjet, string> = {
  renovation_complete: "Rénovation complète",
  cuisine: "Cuisine",
  salle_de_bain: "Salle de bain",
  metallerie: "Métallerie (portail, garde-corps)",
  autre: "Autre",
};

export const LIBELLES_BUDGET: Record<Budget, string> = {
  moins_10k: "Moins de 10 000 €",
  "10_25k": "10 000 à 25 000 €",
  "25_50k": "25 000 à 50 000 €",
  "50_100k": "50 000 à 100 000 €",
  plus_100k: "Plus de 100 000 €",
  ne_sait_pas: "Non précisé",
};

export const LIBELLES_DELAI: Record<Delai, string> = {
  immediat: "Dès que possible",
  moins_3_mois: "Sous 3 mois",
  moins_6_mois: "Sous 6 mois",
  plus_6_mois: "Au-delà de 6 mois",
  renseignement: "Simple renseignement",
};

export const LIBELLES_STATUT_LEAD: Record<StatutLead, string> = {
  nouveau: "Nouveau",
  contacte: "Contacté",
  visite: "Visite",
  gagne: "Gagné",
  perdu: "Perdu",
};

/**
 * Traduit une valeur technique en libellé lisible, et rend la valeur brute
 * si la table ne la connaît pas. Une base plus ancienne que le code peut
 * contenir un budget retiré depuis : mieux vaut afficher `50_100k` qu'un vide
 * qui laisse croire que le champ n'a pas été rempli.
 */
export function libelle(table: Readonly<Record<string, string>>, valeur: string): string {
  return table[valeur] ?? valeur;
}

/** Numéro français, national (0X…) ou international (+33X…), séparateurs tolérés. */
const TELEPHONE_FR = /^(?:\+33[\s.-]?|0)[1-9](?:[\s.-]?\d{2}){4}$/;

/** Ramène un numéro français valide à sa forme E.164 : +33 suivi de 9 chiffres. */
export function normaliserTelephone(valeur: string): string {
  const compact = valeur.replace(/[^\d+]/g, "");
  if (compact.startsWith("+33")) return `+33${compact.slice(3)}`;
  if (compact.startsWith("0")) return `+33${compact.slice(1)}`;
  return compact;
}

export const leadInputSchema = z.object({
  typeProjet: z.enum(TYPES_PROJET),
  pieces: z.array(z.string().trim().min(1).max(60)).max(20).default([]),
  surface: z.number().int().positive().max(2000).optional(),
  delai: z.enum(DELAIS),
  budget: z.enum(BUDGETS),
  nom: z.string().trim().min(2, "Le nom est requis").max(120),
  email: z.string().trim().toLowerCase().email("Adresse email invalide").max(255),
  telephone: z
    .string()
    .trim()
    .regex(TELEPHONE_FR, "Numéro de téléphone français invalide")
    .transform(normaliserTelephone),
  communeTexte: z.string().trim().min(2, "La commune est requise").max(120),
  message: z.string().trim().max(4000).default(""),
  consentement: z.literal(true, {
    errorMap: () => ({ message: "Le consentement est obligatoire" }),
  }),
  pageSource: z.string().trim().max(255).default("/"),
  utmSource: z.string().trim().max(120).optional(),
  utmMedium: z.string().trim().max(120).optional(),
  utmCampaign: z.string().trim().max(120).optional(),
});

export type LeadInput = z.output<typeof leadInputSchema>;

/**
 * Suivi d'une demande depuis l'admin : un statut, des notes, ou les deux.
 *
 * `strict()` refuse tout autre champ plutôt que de l'ignorer : une requête qui
 * croirait pouvoir corriger `email` ou `client_id` par ce chemin doit s'en
 * apercevoir, pas être obéie à moitié.
 */
export const leadSuiviSchema = z
  .object({
    statut: z.enum(STATUTS_LEAD).optional(),
    notes: z.string().max(10_000).optional(),
  })
  .strict()
  .refine((champs) => champs.statut !== undefined || champs.notes !== undefined, {
    message: "Aucun champ à mettre à jour",
  });

export type LeadSuivi = z.output<typeof leadSuiviSchema>;
